import React, { useState } from "react";
import { X, Check, Download, Loader2 } from "lucide-react";
import { useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CheckMark = () => (
  <div className="w-3 h-3 bg-[#22C55E] rounded-[2px] flex items-center justify-center mx-auto">
    <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
  </div>
);

const Dash = () => <span className="text-gray-300 font-medium">—</span>;

const Cell = ({ active, isAllergenZone }) => (
  <td
    className={`py-1.5 px-0.5 text-center border-l border-b border-gray-100 ${isAllergenZone ? "bg-[#FFF5F5]/30" : "bg-[#F6FFFA]/30"
      }`}
  >
    {active ? <CheckMark /> : <Dash />}
  </td>
);

export default function AllergenAccommodationMatrix({
  isOpen,
  onClose,
  restaurantName = "All Restaurants",
  items = [],
  selectedItems = [],
  onFetchAllItems,
}) {
  const [isExporting, setIsExporting] = useState(false);
  // Helper to check for a term in an array or string (case-insensitive)
  const has = (data, term) => {
    if (!data) return false;
    const termLower = term.toLowerCase();
    if (Array.isArray(data)) {
      return data.some((item) => {
        if (!item) return false;
        return item.toLowerCase().includes(termLower);
      });
    }
    if (typeof data === "string") {
      return data.toLowerCase().includes(termLower);
    }
    return false;
  };

  const itemsToShow = useMemo(() => {
    if (selectedItems && selectedItems.length > 0) {
      return items.filter((i) => selectedItems.includes(i.uuid || i.id));
    }
    return items;
  }, [items, selectedItems]);

  const restaurantDisplayName = useMemo(() => {
    if (
      restaurantName &&
      restaurantName !== "All Restaurants" &&
      restaurantName !== "The Garden Bistro"
    ) {
      return restaurantName;
    }
    if (itemsToShow.length > 0) {
      const firstItem = itemsToShow[0];
      return (
        firstItem.restaurantname ||
        firstItem.restaurantNameDisplay ||
        firstItem.restaurant_name ||
        firstItem.restaurantName ||
        restaurantName
      );
    }
    return restaurantName;
  }, [restaurantName, itemsToShow]);

  const matrixData = itemsToShow.map((item) => {
    const allergens = item.allergens || [];
    const accommodations = item.accommodations || [];
    const dietary = item.dietary_restrictions || {};
    const lifestyle = dietary.lifestyle || [];

    return {
      name: item.name || item.product_name || "Unknown Dish",
      category:
        item.typeDisplay ||
        (Array.isArray(item.type) ? item.type[0] : item.category) ||
        "Other",
      allergens: {
        milk: has(allergens, "milk") || has(allergens, "dairy"),
        eggs: has(allergens, "egg"),
        fish: has(allergens, "fish"),
        shellfish:
          has(allergens, "shellfish") ||
          has(allergens, "crustacean") ||
          has(allergens, "mollusc"),
        treeNut:
          has(allergens, "tree nut") ||
          has(allergens, "treenut") ||
          (has(allergens, "nut") && !has(allergens, "peanut")),
        peanuts: has(allergens, "peanut"),
        wheat: has(allergens, "wheat") || has(allergens, "gluten"),
        soy: has(allergens, "soy"),
        sesame: has(allergens, "sesame"),
      },
      accommodations: {
        vegan:
          has(lifestyle, "vegan") ||
          has(accommodations, "vegan") ||
          item.is_vegan === true,
        vegetarian:
          has(lifestyle, "vegetarian") || has(accommodations, "vegetarian"),
        glutenFree:
          has(accommodations, "gluten-free") ||
          has(accommodations, "gluten free") ||
          (!has(allergens, "wheat") && !has(allergens, "gluten")),
        dairyFree:
          has(accommodations, "dairy-free") ||
          has(accommodations, "dairy free"),
        nutFree:
          has(accommodations, "nut-free") || has(accommodations, "nut free"),
        soyFree:
          has(accommodations, "soy-free") || has(accommodations, "soy free"),
        sesameFree:
          has(accommodations, "sesame-free") ||
          has(accommodations, "sesame free"),
      },
    };
  });

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF("l"); // Landscape for matrix
      let itemsToExport = items;

      if (onFetchAllItems) {
        try {
          const fetched = await onFetchAllItems();
          // Ensure we get an array
          if (fetched && Array.isArray(fetched) && fetched.length > 0) {
            itemsToExport = fetched;
          } else if (fetched && fetched.data && Array.isArray(fetched.data)) {
            itemsToExport = fetched.data;
          }
        } catch (err) {
          console.error("Error fetching all items:", err);
          // Fallback to current items
        }
      }

      // Sort items: Primary Category (A-Z), Secondary Name (A-Z)
      const sortedItems = [...itemsToExport].sort((a, b) => {
        const catA = (a.typeDisplay || (Array.isArray(a.type) ? a.type[0] : a.category) || "Other").toLowerCase();
        const catB = (b.typeDisplay || (Array.isArray(b.type) ? b.type[0] : b.category) || "Other").toLowerCase();

        if (catA < catB) return -1;
        if (catA > catB) return 1;

        const nameA = (a.name || a.product_name || "").toLowerCase();
        const nameB = (b.name || b.product_name || "").toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        return 0;
      });

      const allMatrixData = sortedItems.map((item, index) => {
        const allergens = item.allergens || [];
        const accommodations = item.accommodations || [];
        const dietary = item.dietary_restrictions || {};
        const lifestyle = dietary.lifestyle || [];

        const check = (val) => (val ? "✔" : "-");

        return [
          index + 1,
          item.name || item.product_name || "Unknown Dish",
          item.typeDisplay ||
          (Array.isArray(item.type) ? item.type[0] : item.category) ||
          "Other",
          // Allergens
          check(has(allergens, "milk") || has(allergens, "dairy")),
          check(has(allergens, "egg")),
          check(has(allergens, "fish")),
          check(
            has(allergens, "shellfish") ||
            has(allergens, "crustacean") ||
            has(allergens, "mollusc")
          ),
          check(
            has(allergens, "tree nut") ||
            has(allergens, "treenut") ||
            (has(allergens, "nut") && !has(allergens, "peanut"))
          ),
          check(has(allergens, "peanut")),
          check(has(allergens, "wheat") || has(allergens, "gluten")),
          check(has(allergens, "soy")),
          check(has(allergens, "sesame")),
          // Accommodations
          check(
            has(lifestyle, "vegan") ||
            has(accommodations, "vegan") ||
            item.is_vegan === true
          ),
          check(
            has(lifestyle, "vegetarian") || has(accommodations, "vegetarian")
          ),
          check(
            has(accommodations, "gluten-free") ||
            has(accommodations, "gluten free") ||
            (!has(allergens, "wheat") && !has(allergens, "gluten"))
          ),
          check(
            has(accommodations, "dairy-free") ||
            has(accommodations, "dairy free")
          ),
          check(
            has(accommodations, "nut-free") || has(accommodations, "nut free")
          ),
          check(
            has(accommodations, "soy-free") || has(accommodations, "soy free")
          ),
          check(
            has(accommodations, "sesame-free") ||
            has(accommodations, "sesame free")
          ),
        ];
      });

      autoTable(doc, {
        head: [
          [
            { content: "ID", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
            {
              content: "Dish Name",
              rowSpan: 2,
              styles: { valign: "middle", halign: "left" },
            },
            {
              content: "Category",
              rowSpan: 2,
              styles: { valign: "middle", halign: "center" },
            },
            { content: "Allergens Present", colSpan: 9, styles: { halign: "center" } },
            { content: "Inherent Accommodations", colSpan: 7, styles: { halign: "center" } },
          ],
          [
            "Milk",
            "Egg",
            "Fish",
            "Shell",
            "Tree",
            "Pea",
            "Wheat",
            "Soy",
            "Ses",
            "Veg",
            "Vgt",
            "GF",
            "DF",
            "NF",
            "SF",
            "SeF",
          ],
        ],
        body: allMatrixData,
        theme: "striped",
        headStyles: {
          fillColor: [194, 65, 12], // Match #C2410C
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          halign: "center",
        },
        columnStyles: {
          1: { halign: "left", cellWidth: 40 }, // Dish Name
        },
        startY: 25,
        margin: { top: 25 },
        willDrawCell: function (data) {
          // Hide the original text symbol so it doesn't interfere with our drawing
          if (
            data.section === "body" &&
            data.column.index >= 3 &&
            data.cell.raw === "✔"
          ) {
            data.cell.text = [""];
          }
        },
        didDrawCell: function (data) {
          // Draw the custom green checkmark
          if (
            data.section === "body" &&
            data.column.index >= 3 &&
            data.cell.raw === "✔"
          ) {
            const x = data.cell.x + data.cell.width / 2;
            const y = data.cell.y + data.cell.height / 2;

            // Use the green color #16a34a
            doc.setDrawColor(22, 163, 74);
            doc.setLineWidth(0.5);

            // Draw a precise checkmark shape centered in the cell
            doc.line(x - 2, y + 0.5, x - 0.5, y + 2); // Left short stroke
            doc.line(x - 0.5, y + 2, x + 2.5, y - 1); // Right long stroke
          }
        },
        didDrawPage: function (data) {
          // Header
          doc.setFontSize(16);
          doc.setTextColor(0, 0, 0);
          doc.text("Allergen & Accommodation Matrix", 14, 15);
        },
      });

      doc.save("allergen-accommodation-matrix.pdf");
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper components moved outside

  const content = (
    <div
      className={`bg-white w-full ${isOpen ? "max-w-[1400px] h-[90vh] shadow-xl rounded-lg" : "h-full"
        } overflow-hidden flex flex-col relative`}
    >
      {/* Close Button Only if Modal */}
      {isOpen && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-sm hover:bg-gray-50"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      )}

      {/* Header Section */}
      <div
        className={`px-4 pt-4 pb-2 bg-white ${!isOpen && "border-b border-gray-100"
          }`}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <h1 className="text-lg text-start font-bold text-gray-900">
              Allergen & Accommodation Matrix
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Allergens, Health/Belief/Lifestyle Restrictions & Accommodations •
              Restaurant: {restaurantDisplayName}
            </p>
          </div>
          <div className="text-right">
            <h2 className="text-sm font-bold text-gray-900">
              {restaurantDisplayName}
            </h2>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">
              Speak Your Menu
            </p>
          </div>
        </div>

        {/* Legend/Key and Export */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 flex items-center gap-4 py-2 px-3 bg-white rounded-md border border-gray-100 shadow-sm overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#FFF5F5] border border-[#FFE4E4]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Allergen Zone
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#F6FFFA] border border-[#E6F4EA]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Accommodation Zone
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <CheckMark />
              <span className="text-[10px] font-medium text-gray-600">
                = Contains / Inherent
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <span className="text-gray-400 font-bold">—</span>
              <span className="text-[10px] font-medium text-gray-600">
                = Not present / N/A
              </span>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {isExporting ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-[0_0_10px_rgba(0,0,0,0.02)]">
          <table className="w-full border-collapse">
            <thead>
              {/* Top Header Group Row */}
              <tr className="border-b border-gray-200">
                <th
                  colSpan={2}
                  className="bg-[#FFFDF5] py-2 text-center text-xs font-bold text-gray-700 border-r border-[#F3E8C8]"
                >
                  Dish Information
                </th>
                <th
                  colSpan={9}
                  className="bg-[#FFF5F5] py-2 text-center text-xs font-bold text-gray-700 border-r border-[#FFE4E4]"
                >
                  Allergens Present
                </th>
                <th
                  colSpan={7}
                  className="bg-[#F6FFFA] py-2 text-center text-xs font-bold text-gray-700"
                >
                  Inherent Accommodations
                </th>
              </tr>
              {/* Column Headers Row */}
              <tr className="border-b border-gray-200 text-[9px] text-gray-600 font-bold bg-white leading-tight">
                <th className="py-2 px-2 w-[140px] text-left bg-[#FFFDF5] border-r border-gray-100">
                  Dish Name
                </th>
                <th className="py-2 px-1 w-[60px] text-center bg-[#FFFDF5] border-r border-[#F3E8C8]">
                  Category
                </th>

                {/* Allergens */}
                <th className="py-2 px-0.5 w-[35px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Milk
                </th>
                <th className="py-2 px-0.5 w-[35px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Eggs
                </th>
                <th className="py-2 px-0.5 w-[35px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Fish
                </th>
                <th className="py-2 px-0.5 w-[45px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Shellfish
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Tree Nut
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Peanuts
                </th>
                <th className="py-2 px-0.5 w-[35px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Wheat
                </th>
                <th className="py-2 px-0.5 w-[35px] text-center bg-[#FFF5F5] border-l border-gray-100">
                  Soy
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#FFF5F5] border-l border-[#FFE4E4]">
                  Sesame
                </th>

                {/* Accommodations */}
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Vegan
                </th>
                <th className="py-2 px-0.5 w-[50px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Vegetarian
                </th>
                <th className="py-2 px-0.5 w-[50px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Gluten-Fr
                </th>
                <th className="py-2 px-0.5 w-[45px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Dairy-Fr
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Nut-Fr
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Soy-Fr
                </th>
                <th className="py-2 px-0.5 w-[40px] text-center bg-[#F6FFFA] border-l border-gray-100">
                  Sesame-Fr
                </th>
              </tr>
            </thead>
            <tbody>
              {matrixData.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50/50">
                  <td className="py-1.5 px-2 text-[9px] font-extrabold text-[#111827] bg-[#FFFdf5]/30 border-b border-gray-100 border-r border-gray-100 leading-tight">
                    {item.name}
                  </td>
                  <td className="py-1.5 px-1 text-[8px] text-gray-500 text-center font-medium bg-[#FFFdf5]/30 border-b border-gray-100 border-r border-[#F3E8C8]">
                    {item.category}
                  </td>

                  {/* Allergens Data */}
                  <Cell active={item.allergens.milk} isAllergenZone={true} />
                  <Cell active={item.allergens.eggs} isAllergenZone={true} />
                  <Cell active={item.allergens.fish} isAllergenZone={true} />
                  <Cell
                    active={item.allergens.shellfish}
                    isAllergenZone={true}
                  />
                  <Cell active={item.allergens.treeNut} isAllergenZone={true} />
                  <Cell active={item.allergens.peanuts} isAllergenZone={true} />
                  <Cell active={item.allergens.wheat} isAllergenZone={true} />
                  <Cell active={item.allergens.soy} isAllergenZone={true} />
                  <Cell active={item.allergens.sesame} isAllergenZone={true} />

                  {/* Accommodations Data */}
                  <Cell
                    active={item.accommodations.vegan}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.vegetarian}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.glutenFree}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.dairyFree}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.nutFree}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.soyFree}
                    isAllergenZone={false}
                  />
                  <Cell
                    active={item.accommodations.sesameFree}
                    isAllergenZone={false}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-white border-t border-gray-100 flex justify-between items-center text-[10px] text-gray-400">
          <div>
            For training use only • Last updated from Speak Your Menu data sync
            on {new Date().toISOString().split("T")[0]}
          </div>
        </div>
      </div>
    </div>
  );

  if (isOpen) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        {content}
      </div>
    );
  }

  return content;
}
